import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Row, Col, Statistic, Spin, Empty, Select, DatePicker, Segmented,
  Typography, Table, Tag, Tooltip as AntTooltip, Alert, message,
} from "antd";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, Cell,
} from "recharts";
import dayjs from "dayjs";
import {
  getFrameQualityFunnel, getFrameQualityTimeseries, getFrameQualityDevices, getCameras,
} from "../network/api";
import InfoHint from "./common/InfoHint";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const GOOD = "#52c41a";
const WARN = "#faad14";
const CRIT = "#ff4d4f";
const ACCENT = "#13a8a8";
const MUTED = "#8c9bab";

const fmtMs = (ms) => {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};
const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
const rateColor = (r) => (r == null ? MUTED : r >= 0.75 ? GOOD : r >= 0.4 ? WARN : CRIT);

// Same whole-day rule as the door analytics picker: a same-day pick must not send a
// zero-width window, and a "7d" preset must match the same dates picked by hand.
const dayWindow = (range) => ({
  from: range?.[0]?.startOf("day").toISOString(),
  to: range?.[1]?.endOf("day").toISOString(),
});

// The funnel, in pipeline order. Each row is "what was left" and "what fell out here",
// so the drop-off is readable top to bottom rather than needing arithmetic.
const STAGES = [
  ["Captured", "photos uploaded by devices", (f) => f.captured, null],
  ["Scored", "the detector returned a verdict", (f) => f.scored, (f) => f.neverScored + f.scoringFailed],
  ["Chiller found", "cleared the dark / small-box guards", (f) => f.chillerFound, (f) => f.scored - f.chillerFound],
  ["Usable", "also cleared the confidence gate", (f) => f.usable, (f) => f.belowThreshold],
];

const REASON_LABEL = {
  dark: "Too dark",
  small_box: "Box too small",
  no_box: "Nothing detected",
  unattributed: "Reason not recorded",
};
const REASON_HINT = {
  dark: "Lights off, or something covering the lens",
  small_box: "A box was found but covers too little of the frame to be a real chiller",
  no_box: "The detector saw no chiller at all — check aim and obstruction",
  unattributed: "Scored before drop reasons were recorded — run the chiller backfill",
};

const FrameQuality = () => {
  // Device is an OPTIONAL filter (null = whole fleet), just like the date range.
  const [deviceId, setDeviceId] = useState(null);
  const [range, setRange] = useState([dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")]);
  const [type, setType] = useState("daily");
  const [cameras, setCameras] = useState([]);
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCameras(1, 1000, {})
      .then((list) => setCameras(Array.isArray(list) ? list : []))
      .catch(() => setCameras([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const opts = { deviceId: deviceId || undefined, ...dayWindow(range) };
    try {
      const [f, ts, dv] = await Promise.all([
        getFrameQualityFunnel(opts),
        getFrameQualityTimeseries({ ...opts, type }),
        getFrameQualityDevices(opts),
      ]);
      setData(f || null);
      setSeries(Array.isArray(ts?.points) ? ts.points : []);
      setDevices(Array.isArray(dv?.devices) ? dv.devices : []);
    } catch (err) {
      console.error("Failed to load frame quality", err);
      message.error("Could not load frame quality");
      setData(null); setSeries([]); setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId, range, type]);

  useEffect(() => { load(); }, [load]);

  const f = data?.funnel || {};
  const sl = data?.stageLatency;
  const hasData = (f.captured || 0) > 0;

  // Where the round trip goes: the model, the rest of the engine, then transport.
  // Rendered as a stacked breakdown so "scoring is slow" resolves to a specific culprit.
  const stageBars = (() => {
    if (!sl || sl.model?.medianMs == null || sl.roundTrip?.medianMs == null) return null;
    const model = sl.model.medianMs;
    const engineRest = Math.max(0, (sl.engine?.medianMs ?? model) - model);
    const transport = Math.max(0, sl.roundTrip.medianMs - (sl.engine?.medianMs ?? model));
    const total = model + engineRest + transport || 1;
    return [
      { key: "model", label: "Model inference", ms: model, pct: model / total, color: CRIT,
        hint: "The detector itself — what a model swap changes" },
      { key: "engine", label: "Engine overhead", ms: engineRest, pct: engineRest / total, color: WARN,
        hint: "JPEG decode, letterboxing, box decoding inside MarketEye" },
      { key: "transport", label: "Transport", ms: transport, pct: transport / total, color: MUTED,
        hint: "Shipping the image to the engine and the answer back" },
    ];
  })();

  const funnelRows = STAGES.map(([label, hint, left, lost]) => ({
    key: label, label, hint,
    remaining: left(f) || 0,
    lost: lost ? lost(f) || 0 : null,
    share: f.captured ? (left(f) || 0) / f.captured : null,
  }));

  const reasonData = Object.entries(f.dropReasons || {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => ({ key: k, label: REASON_LABEL[k] || k, count: n }))
    .sort((a, b) => b.count - a.count);

  const trend = series.map((p) => ({
    bucket: p.bucket,
    usablePct: p.usableRate == null ? 0 : Math.round(p.usableRate * 100),
    captured: p.captured,
    usable: p.usable,
    avgScoreMs: p.avgScoreMs,
  }));

  const deviceCols = [
    {
      title: "Device", dataIndex: "deviceId", key: "deviceId",
      render: (v, r) => (
        <span>
          <Text strong>{v || "—"}</Text>
          {r.shop ? <div><Text type="secondary" style={{ fontSize: 12 }}>{r.shop}</Text></div> : null}
        </span>
      ),
    },
    { title: "Captured", dataIndex: "captured", key: "captured", width: 100 },
    { title: "Scored", dataIndex: "scored", key: "scored", width: 90 },
    { title: "Usable", dataIndex: "usable", key: "usable", width: 90 },
    {
      title: <span>Usable of scored<InfoHint title="Usable photos as a share of those the detector actually scored. Excludes never-scored frames, so an engine outage doesn't look like a camera/framing problem." /></span>, dataIndex: "usableRateOfScored", key: "rate", width: 150,
      render: (v) => <Tag color={v == null ? "default" : v >= 0.75 ? "green" : v >= 0.4 ? "orange" : "red"}>{pct(v)}</Tag>,
    },
    {
      title: <span>Main drop reason<InfoHint title="The most common reason this device's frames failed, with the count — points you at lighting, aim or obstruction." /></span>, key: "reason", width: 190,
      render: (_, r) => {
        const top = Object.entries(r.dropReasons || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])[0];
        return top ? <Text type="secondary">{REASON_LABEL[top[0]] || top[0]} ({top[1]})</Text> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: <span>Not scored<InfoHint title="Frames the detector never returned a verdict for — “failed” (engine error) or “never” (not yet processed). These are an engine issue, not a bad camera." /></span>, key: "notScored", width: 120,
      render: (_, r) => {
        // An engine outage is not a camera fault — call it out separately so nobody
        // is dispatched to a device that was fine.
        const bits = [];
        if (r.scoringFailed) bits.push(`${r.scoringFailed} failed`);
        if (r.neverScored) bits.push(`${r.neverScored} never`);
        return bits.length ? <Text type="secondary">{bits.join(", ")}</Text> : <Text type="secondary">—</Text>;
      },
    },
  ];

  const modelCols = [
    { title: "Detector", dataIndex: "model", key: "model", render: (v) => <Text code>{v}</Text> },
    { title: "Scored", dataIndex: "scored", key: "scored" },
    { title: "Chiller found", dataIndex: "chillerFound", key: "chillerFound" },
    { title: "Usable", dataIndex: "usable", key: "usable" },
    {
      title: "Usable of scored", dataIndex: "usableRateOfScored", key: "rate",
      render: (v) => <Tag color={v == null ? "default" : v >= 0.75 ? "green" : v >= 0.4 ? "orange" : "red"}>{pct(v)}</Tag>,
    },
    // Model time, not round trip: round trip drifts with image size and link quality, so
    // it is not a fair basis for comparing two detector builds against each other.
    { title: "Median model time", dataIndex: "medianModelMs", key: "modelMs", render: fmtMs },
    { title: "Median round trip", dataIndex: "medianScoreMs", key: "ms", render: fmtMs },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Frame Quality<InfoHint iconStyle={{ fontSize: 14 }} title="Tracks how many captured photos are actually usable for shelf-compliance checks, and where the rest are lost — too dark, no chiller detected, below the confidence gate, or never scored because the detection engine was down. Filter by device and date range above." /></Title>
          <Text type="secondary">
            How much of what the cameras captured is usable for compliance — and where the rest drops out
          </Text>
        </Col>
        <Col>
          <Select
            allowClear showSearch placeholder="All devices" style={{ width: 210, marginRight: 8 }}
            value={deviceId} onChange={setDeviceId} optionFilterProp="label"
            options={cameras.map((c) => ({ value: c.deviceId, label: c.name ? `${c.deviceId} — ${c.name}` : c.deviceId }))}
          />
          <RangePicker value={range} onChange={(r) => r && setRange(r)} allowClear={false} style={{ marginRight: 8 }} />
          <Segmented value={type} onChange={setType} options={[{ label: "Daily", value: "daily" }, { label: "Hourly", value: "hourly" }]} />
        </Col>
      </Row>

      {data?.unknownDevice ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Unknown device" description="No camera matches that device id, so there is nothing to report." />
      ) : null}

      {/* An outage has to be impossible to mistake for badly-aimed cameras. */}
      {f.scoringFailed > 0 ? (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message={`${f.scoringFailed} photo(s) could not be scored`}
          description="The detection engine was asked and did not answer. These are NOT bad frames — check the MarketEye gateway before dispatching anyone to a device." />
      ) : null}

      <Spin spinning={loading}>
        {!hasData ? (
          <Empty description="No photos captured in this window" style={{ margin: "48px 0" }} />
        ) : (
          <>
            <Row gutter={[12, 12]}>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title={<span>Captured<InfoHint title="Total photos uploaded by devices in this window — the top of the funnel, before any quality checks." /></span>} value={f.captured} />
                  <Text type="secondary" style={{ fontSize: 12 }}>{data.deviceCount} device(s)</Text>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title={<span>Usable<InfoHint title="Photos that passed every check and can be used for compliance — a chiller was found and the detector's confidence cleared the gate. Shown as usable / total captured." /></span>} value={f.usable}
                    valueStyle={{ color: rateColor(f.usableRate) }}
                    suffix={<span style={{ fontSize: 14, color: MUTED }}>/ {f.captured}</span>} />
                  <Text type="secondary" style={{ fontSize: 12 }}>{pct(f.usableRate)} of everything captured</Text>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <AntTooltip title="Excludes photos the detector never scored, so an engine outage doesn't read as a framing problem">
                    <Statistic title="Usable of scored" value={pct(f.usableRateOfScored)}
                      valueStyle={{ color: rateColor(f.usableRateOfScored) }} />
                  </AntTooltip>
                  <Text type="secondary" style={{ fontSize: 12 }}>gate ≥ {data.gateThreshold}</Text>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <AntTooltip title="Model time is the part a detector swap is responsible for; the rest is decode plus shipping the image to the engine and back">
                    <Statistic title="Median score time" value={fmtMs(data.scoreLatency?.medianMs)} />
                  </AntTooltip>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    p90 {fmtMs(data.scoreLatency?.p90Ms)}
                    {sl?.model?.medianMs != null ? <> · model {fmtMs(sl.model.medianMs)}</> : null}
                  </Text>
                </Card>
              </Col>
            </Row>

            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
              <Col xs={24} lg={12}>
                <Card size="small" title={<span>Pipeline funnel<InfoHint title="Follows photos through each quality stage — Captured → Scored → Chiller found → Usable — showing how many survive and how many drop out at each step." /></span>}>
                  <Table
                    size="small" pagination={false} dataSource={funnelRows} rowKey="key"
                    columns={[
                      {
                        title: "Stage", dataIndex: "label", key: "label",
                        render: (v, r) => (
                          <span>
                            <Text strong>{v}</Text>
                            <div><Text type="secondary" style={{ fontSize: 12 }}>{r.hint}</Text></div>
                          </span>
                        ),
                      },
                      { title: "Remaining", dataIndex: "remaining", key: "remaining", width: 100 },
                      {
                        title: "Lost here", dataIndex: "lost", key: "lost", width: 100,
                        render: (v) => (v == null ? <Text type="secondary">—</Text> : v > 0 ? <Text type="danger">−{v}</Text> : <Text type="secondary">0</Text>),
                      },
                      {
                        title: "% of captured", dataIndex: "share", key: "share", width: 120,
                        render: (v) => <Tag color={v == null ? "default" : v >= 0.75 ? "green" : v >= 0.4 ? "orange" : "red"}>{pct(v)}</Tag>,
                      },
                    ]}
                  />
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card size="small" title={<span>Why frames were dropped<InfoHint title="The reasons scored photos failed to become usable — too dark, box too small, nothing detected, or reason not recorded. Tells you whether it's a lighting, aim or coverage problem." /></span>}>
                  {reasonData.length === 0 ? (
                    <Empty description="Nothing dropped" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={reasonData} layout="vertical" margin={{ left: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis type="category" dataKey="label" width={130} />
                          <Tooltip formatter={(v) => [`${v} frame(s)`, "Dropped"]} />
                          <Bar dataKey="count">
                            {reasonData.map((r) => (
                              <Cell key={r.key} fill={r.key === "unattributed" ? MUTED : CRIT} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <div style={{ marginTop: 8 }}>
                        {reasonData.map((r) => (
                          <div key={r.key}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <b>{r.label}:</b> {REASON_HINT[r.key] || ""}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Card>
              </Col>
            </Row>

            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
              <Col xs={24} lg={14}>
                <Card size="small" title={<span>Usable rate over time<InfoHint title="Trend of the usable-photo percentage (left axis) against how many were captured (right axis). Watch for dips that coincide with a deployment or site change." /></span>}>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" />
                      <YAxis yAxisId="l" domain={[0, 100]} unit="%" />
                      <YAxis yAxisId="r" orientation="right" />
                      <Tooltip />
                      <Line yAxisId="l" type="monotone" dataKey="usablePct" name="Usable %" stroke={ACCENT} strokeWidth={2} dot={false} />
                      <Line yAxisId="r" type="monotone" dataKey="captured" name="Captured" stroke={MUTED} strokeWidth={1} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col xs={24} lg={10}>
                <Card size="small" title={<span>By detector build<InfoHint title="Compares the AI detector versions in use — how many frames each scored, its usable rate and its speed. Use it to judge whether swapping the model helped." /></span>}
                  extra={<Text type="secondary" style={{ fontSize: 12 }}>swap impact</Text>}>
                  {(data.byModel || []).length === 0 ? (
                    <Empty description="No model recorded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <Table size="small" pagination={false} rowKey="model"
                      dataSource={data.byModel} columns={modelCols} />
                  )}
                </Card>
              </Col>
            </Row>

            <Card size="small" title={<span>Where scoring time goes<InfoHint title="Breaks the median time to score a photo into model inference, engine overhead (decode etc.) and transport (shipping the image and answer). Shows whether the model or the plumbing is the bottleneck." /></span>} style={{ marginTop: 12 }}
              extra={
                sl?.modelShareOfRoundTrip != null ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    model is {pct(sl.modelShareOfRoundTrip)} of the round trip
                  </Text>
                ) : null
              }>
              {!stageBars ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No stage timings yet — photos scored before stage timing shipped only report a total"
                />
              ) : (
                <Row gutter={[16, 8]}>
                  {stageBars.map((s) => (
                    <Col xs={24} md={8} key={s.key}>
                      <AntTooltip title={s.hint}>
                        <div>
                          <Text strong>{s.label}</Text>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                            <Text style={{ fontSize: 20, color: s.color }}>{fmtMs(s.ms)}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{pct(s.pct)}</Text>
                          </div>
                          <div style={{ height: 8, background: "#f0f2f5", borderRadius: 4, marginTop: 4 }}>
                            <div style={{ height: 8, width: `${Math.round(s.pct * 100)}%`,
                                          background: s.color, borderRadius: 4 }} />
                          </div>
                        </div>
                      </AntTooltip>
                    </Col>
                  ))}
                  <Col span={24}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Median per photo, over {sl.model.n} scored frame(s). Round trip{" "}
                      {fmtMs(sl.roundTrip.medianMs)} (p90 {fmtMs(sl.roundTrip.p90Ms)}).{" "}
                      {sl.modelShareOfRoundTrip != null && sl.modelShareOfRoundTrip > 0.75
                        ? "The model dominates — transport tuning will not help much here."
                        : "A large share is outside the model — look at image size and the link before the model."}
                    </Text>
                  </Col>
                </Row>
              )}
            </Card>

            <Card size="small" title={<span>Devices needing attention<InfoHint title="Per-device breakdown sorted worst-first by usable rate, so you can see which cameras to fix and why (main drop reason, unscored counts)." /></span>} style={{ marginTop: 12 }}
              extra={<Text type="secondary" style={{ fontSize: 12 }}>worst usable rate first</Text>}>
              <Table
                size="small" rowKey="cameraId" dataSource={devices} columns={deviceCols}
                pagination={{ pageSize: 10, showSizeChanger: false }}
              />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
};

export default FrameQuality;
